import type { ReactNode } from 'react';
import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRetweet } from '@soundxyz/font-awesome-new/pro-regular-svg-icons/faArrowsRetweet.js';
import {
  faArrowUpFromBracket,
  faComment,
  faHeart,
} from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faHeart as faHeartSolid } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { theme } from '@components-shared/src/stitches';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import type { CurrentListeningParty } from '@hooks/usePlayButton';
import type { DropdownAlign } from '../Dropdowns/GlobalDropdown';
import { MoreDropdown } from '../Dropdowns/MoreDropdown';

export const ActionBarV2 = ({
  isLiked = false,
  isLikeButtonDisabled = false,
  onLikeButtonClick,
  onPostButtonClick,
  onShareButtonClick,
  onCommentButtonClick,
  isPlaying,
  isPlayButtonLoading = false,
  isPlayButtonDisabled = false,
  onPlayButtonClick,
  moreDropdownItems,
  playButtonRef,
  isPreview = false,
  align = 'end',
  currentListeningParty,
}: {
  isLiked?: boolean;
  isLikeButtonDisabled?: boolean;
  onLikeButtonClick?: () => void;
  onPostButtonClick: () => void;
  onShareButtonClick?: () => void;
  onCommentButtonClick?: () => void;
  isPlaying?: boolean;
  isPlayButtonLoading?: boolean;
  isPlayButtonDisabled?: boolean;
  onPlayButtonClick?: () => void;
  moreDropdownItems?: ReactNode;
  playButtonRef?: (node?: Element | null) => void;
  isPreview?: boolean;
  align?: DropdownAlign;
  className?: string;
  currentListeningParty: CurrentListeningParty;
}) => {
  const postsButton = useMemo(
    () => (
      <IconButton
        iconSize={24}
        icon={<FontAwesomeIcon icon={faArrowsRetweet} color="base500" />}
        onClick={onPostButtonClick}
      />
    ),
    [onPostButtonClick],
  );

  return (
    <div className="flex h-[84px] w-full items-center justify-between pb-0 pt-3 md:h-auto md:pb-6 md:pt-6 lt:pb-0">
      <div className="flex items-center gap-2.5">
        {(onLikeButtonClick || isPreview) && (
          <IconButton
            icon={
              <FontAwesomeIcon
                icon={isLiked ? faHeartSolid : faHeart}
                color={
                  theme.colors[
                    isLiked
                      ? 'fuchsia500'
                      : isLikeButtonDisabled || isPreview
                      ? 'neutral300'
                      : 'base500'
                  ].value
                }
              />
            }
            onClick={onLikeButtonClick}
            disabled={isLikeButtonDisabled || isPreview}
          />
        )}
        {onCommentButtonClick && (
          <IconButton
            icon={<FontAwesomeIcon icon={faComment} color="base500" />}
            onClick={onCommentButtonClick}
          />
        )}
        {postsButton}
        <IconButton
          icon={<FontAwesomeIcon icon={faArrowUpFromBracket} />}
          onClick={onShareButtonClick}
          disabled={isPreview}
        />
        <MoreDropdown align={align} disabled={isPreview}>
          {moreDropdownItems}
        </MoreDropdown>
      </div>
      <div className="flex md:hidden">
        <PlayButton
          playing={isPlaying}
          onClick={onPlayButtonClick}
          color="black"
          size="m"
          ref={playButtonRef}
          loading={isPlayButtonLoading}
          disabled={isPlayButtonDisabled}
          currentListeningParty={currentListeningParty}
        />
      </div>
    </div>
  );
};
